// Browser helpers for ShardNexus rooms. The flow is: get a player (or guest) token, ask the
// match API for a room, then open wss:// straight to the game process on its host.
//
//   const snx = createMatchClient({api: 'https://snx.example.com', project: 'wreck-shift', fleet: 'games-eu'});
//   const room = await snx.quickplay({mode: 'road-war'});      // or snx.createPrivate(), snx.join(code)
//   const ws = snx.connect(room);                               // wss://h7.play.example.com:24001/play?room=…&token=…
//
// Every call rejects with an Error carrying `.status`, `.reason` (the body's `reason` or the
// X-Reject-Reason header, e.g. host_network_full, match_started), `.retryAfterMs` (Retry-After),
// `.retryable` (busy: back off and ask again) and `.terminal` (this room or seat will not take you:
// find another match). `quickplayWithRetry()` / `withRetry()` do the backing off; `closeKind()`
// sorts a WebSocket close into terminal | retry | resume | clean; `playerMessage()` words a reason.
//
// Every request (guest(), quickplay, join, polling a starting room) is bounded by `requestTimeoutMs` (default 10 s):
// a network whose TCP to the match API hangs gets status 0 with `.code` 'timeout' (no answer in time) or
// 'unreachable' (the fetch failed: offline, DNS, refused, CORS), retryable, so withRetry keeps trying within its
// maxWaitMs and then gives up instead of awaiting forever. `isUnreachable(error)` tells these apart from a refusal:
// show "Couldn't reach the match server" and offer offline play.

const STORE_KEY = 'snx:session';

// Busy, not refused: the same request may succeed after Retry-After (with jitter). 0 = network failure.
const RETRY_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504]);
// Room-level refusals that will not change for this room or seat: leave it and find another match.
const TERMINAL_REASONS = new Set(['match_started', 'room_released', 'room_unknown', 'room_closed', 'room_ended',
  'resume_expired', 'survival_started', 'portal_room', 'ranked_late', 'unavailable', 'banned', 'version_mismatch',
  'replaced']);
// Busy: the same room (or the next quickplay) may take the player after a wait.
const RETRY_REASONS = new Set(['room_full', 'room_limit', 'draining', 'host_full', 'host_network_full', 'host_saturated',
  'partition_overloaded', 'admission_throttled', 'no_capacity', 'rate_limited', 'too_many_connections', 'slow_client',
  'try_again', 'allocation_failed']);
// Rooms the server picked for the player (quickplay, a new private room): a full room (409 room_full) or an
// allocation the server dropped while the player waited (poll 404/410: its process never became ready) is not a
// match the player chose, so ask again for another one. A join by code or a resume keeps 404/409/410 terminal.
const PICKED_RETRY_REASONS = new Set(['room_full', 'allocation_failed']);

// The reason a close (or refusal text) names, in the servers' reason vocabulary (X-Reject-Reason / body reason);
// null when it names none.
export function closeReason(code, reason = '') {
  const text = String(reason ?? '').trim().toLowerCase();
  const token = text.replace(/[\s-]+/g, '_');
  if (TERMINAL_REASONS.has(token) || RETRY_REASONS.has(token)) return token;
  if (code === 4409) return 'match_started';
  if (code === 4410) return 'room_released';
  if (text) {
    if (/backlog|too slow/.test(text)) return 'slow_client';
    if (/already started|has started|match started|room started|in progress/.test(text)) return 'match_started';
    if (/expired/.test(text)) return 'resume_expired';
    if (/ended|closed|released/.test(text)) return 'room_released';
    if (/network.full/.test(text)) return 'host_network_full';
    if (/\bfull\b/.test(text)) return 'room_full';
    if (/drain|shutting down|restart|updat/.test(text)) return 'draining';
  }
  return code === 1013 ? 'try_again' : null;
}

// What to do after a WebSocket close (code, reason text):
//   clean:    1000 normal closure - the server ended the session on purpose; nothing to reconnect.
//   terminal: the room will not take this seat back (4xxx such as 4409 match_started / 4410 room_released, 1008
//             policy, 1002/1003/1007 protocol, or a terminal reason such as "room started") - stop reconnecting,
//             tell the player (playerMessage(closeReason(...))), offer "find another match", snx.forget().
//   retry:    1013 try-again-later (busy server, slow client) - back off 5-8 s, then rejoin the held seat.
//   resume:   an unexpected drop (1006, 1001 going away, 1011/1012 server fault/restart, ...) - rejoin the held
//             seat with the reconnect token while the reconnect window lasts, quietly ("reconnecting...").
export function closeKind(code, reason = '') {
  if (code === 1000) return 'clean';
  const why = closeReason(code, reason);
  if (why && TERMINAL_REASONS.has(why)) return 'terminal';
  if (code >= 4000 && code < 5000) return 'terminal';
  if (code === 1008 || code === 1002 || code === 1003 || code === 1007 || code === 1010) return 'terminal';
  if (code === 1013) return 'retry';
  return 'resume';
}

// A match-API answer as {retryable, terminal}. terminal: this room or seat is gone (404/409/410 or a terminal
// reason); retryable: busy (503 capacity, 429, gateway errors, network failure).
export function classifyStatus(status, reason = null) {
  if (reason && TERMINAL_REASONS.has(String(reason))) return {retryable: false, terminal: true};
  if (RETRY_STATUS.has(Number(status) || 0)) return {retryable: true, terminal: false};
  return {retryable: false, terminal: status === 404 || status === 409 || status === 410};
}

const MESSAGES = {
  room_full: 'That match is full.',
  room_limit: 'Servers are busy.',
  host_full: 'Servers are busy.',
  host_saturated: 'Servers are busy.',
  host_network_full: 'Servers are busy; more are starting.',
  no_capacity: 'Servers are busy; more are starting.',
  partition_overloaded: 'Matchmaking is busy.',
  admission_throttled: 'Lots of players are joining right now.',
  rate_limited: 'Too many attempts.',
  too_many_connections: 'Too many connections from your network.',
  draining: 'The server is updating.',
  slow_client: 'Your connection fell behind.',
  try_again: 'The server is busy.',
  allocation_failed: 'Servers are busy; more are starting.',
  network: 'Can\'t reach the match server.',
  timeout: 'Can\'t reach the match server.',
  match_started: 'That match has already started.',
  survival_started: 'That match has already started.',
  ranked_late: 'That match has already started.',
  room_released: 'That match has ended.',
  room_closed: 'That match has ended.',
  room_ended: 'That match has ended.',
  room_unknown: 'That match no longer exists.',
  resume_expired: 'Your seat in that match has expired.',
  unavailable: 'That match is no longer available.',
  version_mismatch: 'A new version of the game is available. Please reload.',
  banned: 'You cannot join this match.',
  replaced: 'You joined from another tab or device.',
};

// A short player-facing sentence for a reason (closeReason() / error.reason). retryInMs appends
// "Retrying in Ns…"; for a terminal reason show it next to a "Find another match" button.
export function playerMessage(reason, {retryInMs = null, status = null} = {}) {
  let text = MESSAGES[reason] ?? (status === 404 ? 'That match no longer exists.'
    : status === 409 || status === 410 ? 'That match is no longer available.'
      : status === 0 ? MESSAGES.network : 'Servers are busy.');
  if (retryInMs != null) text += ` Retrying in ${Math.max(1, Math.ceil(retryInMs / 1000))}s…`;
  return text;
}

// Exponential backoff (1 s doubling to 10 s, jittered to 50-100 %), never shorter than the server's Retry-After
// hint (itself jittered up to +20 %, so a crowd told "2 s" does not come back in lockstep; capped at 30 s).
export function backoffMs(attempt, {retryAfterMs = null, random = Math.random, baseMs = 1000, capMs = 10000} = {}) {
  const r = Math.min(1, Math.max(0, Number(random()) || 0));
  const exp = Math.min(capMs, baseMs * 2 ** Math.min(Math.max(0, attempt), 10)) * (0.5 + 0.5 * r);
  const hint = retryAfterMs > 0 ? Math.min(30000, retryAfterMs) * (1 + 0.2 * r) : 0;
  return Math.round(Math.max(exp, hint));
}

const defaultSleep = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) return reject(signal.reason ?? new Error('aborted'));
  const stop = () => { clearTimeout(timer); reject(signal.reason ?? new Error('aborted')); };
  const timer = setTimeout(() => { signal?.removeEventListener?.('abort', stop); resolve(); }, ms);
  signal?.addEventListener?.('abort', stop, {once: true});
});

// Runs fn(attempt, {deadline, signal}) until it resolves, throws a non-retryable error, or the next wait would pass maxWaitMs (then
// the last error is rethrown with .gaveUp = true and .waitedMs). Before each wait it calls
// onWaiting({attempt, retryInMs, waitedMs, reason, status, message, error}) so the UI can show
// "Servers are busy. Retrying in 4s…". deadline (started + maxWaitMs on the now() clock) lets fn stop its own
// waiting, e.g. quickplay({deadline, signal}) polling a starting room.
export async function withRetry(fn, {maxWaitMs = 60000, onWaiting, signal, sleep = defaultSleep, random = Math.random, now = () => Date.now()} = {}) {
  const started = now();
  const deadline = started + maxWaitMs;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn(attempt, {deadline, signal});
    } catch (error) {
      if (!error?.retryable || signal?.aborted) throw error;
      const waitedMs = now() - started;
      const retryInMs = backoffMs(attempt, {retryAfterMs: error.retryAfterMs, random});
      if (waitedMs + retryInMs > maxWaitMs) { error.gaveUp = true; error.waitedMs = waitedMs; throw error; }
      onWaiting?.({attempt: attempt + 1, retryInMs, waitedMs, reason: error.reason ?? null, status: error.status,
        message: playerMessage(error.reason, {retryInMs, status: error.status}), error});
      await sleep(retryInMs, signal);
    }
  }
}

// The match API could not be reached at all (code 'unreachable': the fetch failed) or did not answer within
// requestTimeoutMs (code 'timeout'). Both are status 0 and retryable; the player's network may be the cause.
export function isUnreachable(error) {
  return error?.code === 'unreachable' || error?.code === 'timeout';
}

function matchError(message, status, reason, retryAfterMs) {
  const error = new Error(message);
  error.status = status;
  error.reason = reason || null;
  error.retryAfterMs = retryAfterMs || null;
  return Object.assign(error, classifyStatus(status, error.reason));
}
const retryAfterOf = (res, data) => Number(res.headers?.get?.('retry-after') || 0) * 1000 || Number(data?.retryAfterMs) || null;
const reasonOf = (res, data) => (typeof data?.reason === 'string' && data.reason) || res.headers?.get?.('x-reject-reason') || null;

export function createMatchClient({api, project, fleet, fetchImpl = globalThis.fetch?.bind(globalThis), storage, socketPath = '/play', sleep, random, now, requestTimeoutMs = 10000} = {}) {
  const wait = sleep ?? defaultSleep, clock = now ?? (() => Date.now());
  if (!api || !project || !fleet) throw new Error('createMatchClient needs api, project and fleet');
  // Reading sessionStorage throws in a storage-blocked iframe; resolve it here instead of in the parameter default.
  if (storage === undefined) { try { storage = globalThis.sessionStorage; } catch { storage = null; } }
  const base = api.replace(/\/$/, '');
  const retryDefaults = {sleep, random, now};
  for (const k of Object.keys(retryDefaults)) if (retryDefaults[k] === undefined) delete retryDefaults[k];
  let token = null;

  const unreachable = (code, cause, ms = 0) => {
    const error = code === 'timeout'
      ? matchError(`The match service did not answer within ${Math.max(1, Math.round(ms / 1000))}s`, 0, 'timeout', null)
      : matchError('Cannot reach the match service', 0, 'network', null);
    error.code = code;
    if (cause !== undefined) error.cause = cause;
    return error;
  };
  // One request and its body: {res, data}. A network failure (offline, DNS, a CORS refusal mid-deploy) is status 0,
  // reason "network", code 'unreachable'; no answer (headers and body) within requestTimeoutMs, or less when the
  // caller's deadline is nearer (never under 1 s), aborts the fetch and is status 0, reason "timeout", code 'timeout'.
  // Both are retryable. The timeout does not rely on fetch honouring the AbortSignal: a fetch that never settles
  // still ends here. The caller's signal aborts the request at once and rethrows its reason.
  const send = (url, init = {}, {signal, deadline = null} = {}) => {
    let ms = Number(requestTimeoutMs) > 0 ? Number(requestTimeoutMs) : 0;
    if (ms && deadline != null) ms = Math.min(ms, Math.max(1000, deadline - clock()));
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer = null, onAbort = null;
    const limit = new Promise((_, reject) => {
      if (ms) timer = setTimeout(() => { const error = unreachable('timeout', undefined, ms); controller?.abort(error); reject(error); }, ms);
      if (signal) {
        onAbort = () => { const why = signal.reason ?? new Error('aborted'); controller?.abort(why); reject(why); };
        if (signal.aborted) onAbort(); else signal.addEventListener?.('abort', onAbort, {once: true});
      }
    });
    const attempt = (async () => {
      let res;
      try { res = await fetchImpl(url, controller ? {...init, signal: controller.signal} : init); } catch (cause) {
        if (controller?.signal.aborted) throw controller.signal.reason ?? cause;
        throw unreachable('unreachable', cause);
      }
      const data = await res.json().catch(() => ({}));
      return {res, data};
    })();
    attempt.catch(() => {}); limit.catch(() => {});
    return Promise.race([attempt, limit]).finally(() => {
      clearTimeout(timer);
      if (onAbort) signal.removeEventListener?.('abort', onAbort);
    });
  };

  // picked: the server chooses the room (quickplay, createPrivate), see PICKED_RETRY_REASONS. deadline/signal bound
  // the wait for a starting room.
  const request = async (path, body, auth = true, {picked = false, deadline = null, signal} = {}) => {
    const {res, data} = await send(`${base}${path}`, {
      method: 'POST',
      headers: {'content-type': 'application/json', ...(auth && token ? {authorization: `Bearer ${token}`} : {}),
        'idempotency-key': body?.idempotencyKey ?? crypto.randomUUID()},
      body: JSON.stringify(body ?? {}),
    }, {signal, deadline});
    if (res.status === 201 || res.status === 200) return data;
    if (res.status === 202 && data.poll) return poll(data.poll, {picked, deadline, signal});
    const error = matchError(data.error || `match request failed (${res.status})`, res.status, reasonOf(res, data), retryAfterOf(res, data));
    if (picked && PICKED_RETRY_REASONS.has(error.reason)) Object.assign(error, {retryable: true, terminal: false});
    throw error;
  };

  // A cold-started process answers 202 with a poll URL; it is usually ready within a second or two. The wait stops
  // at the caller's deadline (quickplayWithRetry's maxWaitMs) or signal with a retryable "servers busy" error, so a
  // capped wait is never stretched by a slow allocation. A 404/410 while polling a room the server picked means it
  // released the reservation (the process never became ready): retryable allocation_failed, ask again.
  const poll = async (url, {picked = false, deadline = null, signal} = {}) => {
    const timedOut = () => Object.assign(matchError('servers are busy: no room became ready in time', 503, 'no_capacity', null), {gaveUp: true});
    for (let attempt = 0; attempt < 40; attempt++) {
      let delay = Math.min(250 * 2 ** Math.min(attempt, 3), 1500);
      if (deadline != null) {
        const left = deadline - clock();
        if (left <= 0) throw timedOut();
        delay = Math.min(delay, left);
      }
      await wait(delay, signal);
      const {res, data} = await send(url.startsWith('http') ? url : `${base}${url}`, {headers: token ? {authorization: `Bearer ${token}`} : {}}, {signal, deadline});
      if (res.status === 200 && data.status === 'ready') return data;
      if (res.status >= 400) {
        const error = matchError(data.error || 'allocation failed', res.status, reasonOf(res, data), retryAfterOf(res, data));
        if (picked && (res.status === 404 || res.status === 410 || PICKED_RETRY_REASONS.has(error.reason)))
          Object.assign(error, {reason: res.status === 404 || res.status === 410 ? 'allocation_failed' : error.reason, retryable: true, terminal: false});
        throw error;
      }
      if (deadline != null && clock() >= deadline) throw timedOut();
    }
    throw matchError('room did not become ready', 504, null, null);
  };

  const remember = room => { try { storage?.setItem(STORE_KEY, JSON.stringify({room, at: Date.now()})); } catch { /* storage may be blocked */ } return room; };

  return {
    // Portal/platform session token, or a guest token minted by the project's public endpoint.
    setToken(value) { token = value; },
    getToken() { return token; },
    // Bounded like every request (requestTimeoutMs); wrap it in withRetry to wait out an unreachable API, e.g.
    // snx.withRetry((n, bounds) => snx.guest(bounds), {maxWaitMs: 20000, onWaiting, signal}).
    async guest({deadline, signal} = {}) { const data = await request(`/api/v1/projects/${encodeURIComponent(project)}/guest-tokens`, {}, false, {deadline, signal}); token = data.token; return data; },
    // deadline (on the client's now() clock) and signal bound the wait for a starting room (see poll).
    quickplay({mode, party, deadline, signal} = {}) { return request('/api/v1/match/rooms', {project, fleet, mode, kind: 'quickplay', party}, true, {picked: true, deadline, signal}).then(remember); },
    // quickplay() that waits out busy answers (503 host_network_full, partition_overloaded, network errors, ...):
    // backs off with jitter honouring Retry-After, calls onWaiting(state) before each wait (see withRetry), and
    // rethrows after maxWaitMs (default 60 s) or on a non-retryable error. A full room (409 room_full) or a dropped
    // allocation (allocation_failed) asks again too. The whole wait, polling a starting room included, ends at
    // maxWaitMs with a retryable error marked gaveUp ("Servers are busy"). signal: an AbortSignal to stop waiting.
    quickplayWithRetry({mode, party, maxWaitMs, onWaiting, signal} = {}) {
      return withRetry((attempt, bounds) => this.quickplay({mode, party, ...bounds}), {...retryDefaults, maxWaitMs, onWaiting, signal});
    },
    // Any call under the same policy, e.g. snx.withRetry(() => snx.join(code), {onWaiting}).
    withRetry(fn, options = {}) { return withRetry(fn, {...retryDefaults, ...options}); },
    createPrivate({mode, deadline, signal} = {}) { return request('/api/v1/match/rooms', {project, fleet, mode, kind: 'private'}, true, {picked: true, deadline, signal}).then(remember); },
    join(code) { return request('/api/v1/match/rooms/join', {project, fleet, code: String(code).trim().toUpperCase()}).then(remember); },
    // Rejoin the room from this tab's last session within the reconnect window (null when there is none). A
    // terminal error (404/409/410: the seat or room is gone) also forgets the session: find another match.
    async resume() {
      let saved = null;
      try { saved = JSON.parse(storage?.getItem(STORE_KEY) ?? 'null'); } catch { /* ignore */ }
      if (!saved?.room?.reconnectToken) return null;
      return request('/api/v1/match/rooms/join', {project, fleet, reconnectToken: saved.room.reconnectToken}).then(remember, error => {
        if (error.terminal) this.forget();
        throw error;
      });
    },
    socketUrl(room) {
      const {host, port, path = socketPath} = room.endpoint;
      const q = new URLSearchParams({room: room.roomKey, token: room.joinToken});
      return `wss://${host}${port && port !== 443 ? `:${port}` : ''}${path}?${q}`;
    },
    connect(room, protocols) { return new WebSocket(this.socketUrl(room), protocols); },
    forget() { try { storage?.removeItem(STORE_KEY); } catch { /* ignore */ } },
  };
}
